import React, { useEffect, useRef, useState } from 'react';
import type { CommentKind, Task, TaskComment } from '../../../../types';
import { taskService } from '../../services/taskService';
import { useTaskStore } from '../../../../stores/taskStore';
import { websocketService, TaskEventType } from '../../../../services/websocket';
import { getUserDisplayName } from '../../../../utils/userUtils';
import { Markdown } from '../../../../components/ui/Markdown';
import { TaskEditForm } from '../TaskEdit/TaskEditForm';
import { ModalShell } from '../../../../components/ui/ModalShell';

interface TaskDetailModalProps {
  task: Task;
  onClose: () => void;
}

const STATUS: Record<Task['status'], { label: string; className: string }> = {
  TODO: { label: '待办', className: 'bg-gray-100 text-gray-700' },
  IN_PROGRESS: { label: '进行中', className: 'bg-blue-100 text-blue-700' },
  REVIEW: { label: '待验收', className: 'bg-amber-100 text-amber-800' },
  BLOCKED: { label: '受阻', className: 'bg-red-100 text-red-700' },
  DONE: { label: '已完成', className: 'bg-green-100 text-green-700' },
};

const PRIORITY: Record<Task['priority'], { label: string; className: string }> = {
  HIGH: { label: '高', className: 'bg-red-50 text-red-600' },
  MEDIUM: { label: '中', className: 'bg-amber-50 text-amber-600' },
  LOW: { label: '低', className: 'bg-green-50 text-green-600' },
};

const SIZE: Record<Task['size'], string> = { SMALL: '小（1-2 小时）', MEDIUM: '中（半天）', LARGE: '大（1 天+）' };

const KIND_LABEL: Record<CommentKind, { text: string; className: string }> = {
  NOTE: { text: '评论', className: 'bg-gray-100 text-gray-600' },
  PLAN: { text: '拆解', className: 'bg-blue-100 text-blue-700' },
  PROGRESS: { text: '进度', className: 'bg-cyan-100 text-cyan-700' },
  REVIEW: { text: '审查', className: 'bg-purple-100 text-purple-700' },
  VERIFY: { text: '验证', className: 'bg-green-100 text-green-700' },
  REPORT: { text: '验收报告', className: 'bg-amber-100 text-amber-800' },
  BLOCKED: { text: '受阻', className: 'bg-red-100 text-red-700' },
};

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('zh-CN', { year: 'numeric', month: 'numeric', day: 'numeric' }) : '—';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="text-xs text-gray-400 mb-1">{label}</div>
    <div className="text-sm text-gray-800">{children}</div>
  </div>
);

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ task, onClose }) => {
  const updateTask = useTaskStore(state => state.updateTask);
  const taskTypes = useTaskStore(state => state.taskTypes);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const editingRef = useRef(false);
  editingRef.current = editing;
  // 编辑表单先处理了同一次 Esc（退回查看），弹窗这次不再跟着关
  const justLeftEdit = useRef(false);
  const leaveEdit = () => {
    justLeftEdit.current = true;
    setEditing(false);
    setTimeout(() => { justLeftEdit.current = false; }, 0);
  };
  const bottomRef = useRef<HTMLDivElement>(null);

  // 首次打开停在顶部（先看描述和完成标准）；之后有新评论才滚到底
  const scrollToEnd = () => setTimeout(() => bottomRef.current?.scrollIntoView({ block: 'nearest' }), 50);

  // 读取评论，并实时接收新评论
  useEffect(() => {
    let cancelled = false;
    taskService.getComments(task.id)
      .then(list => { if (!cancelled) setComments(list); })
      .catch(error => console.error('读取评论失败:', error))
      .finally(() => { if (!cancelled) setLoading(false); });

    const handleCreated = (data: { taskId?: string; comment?: TaskComment }) => {
      if (data.taskId !== task.id || !data.comment) return;
      setComments(prev => (prev.some(c => c.id === data.comment!.id) ? prev : [...prev, data.comment!]));
      scrollToEnd();
    };
    websocketService.onTaskEvent(TaskEventType.COMMENT_CREATED, handleCreated as any);

    return () => {
      cancelled = true;
      websocketService.offTaskEvent(TaskEventType.COMMENT_CREATED, handleCreated as any);
    };
  }, [task.id]);

  // Esc 关闭；编辑中的 Esc 由编辑表单自己处理（退回查看）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !editingRef.current && !justLeftEdit.current) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const addComment = async (): Promise<boolean> => {
    const content = text.trim();
    if (!content) return true;
    try {
      const created = await taskService.addComment(task.id, content);
      setComments(prev => (prev.some(c => c.id === created.id) ? prev : [...prev, created]));
      setText('');
      scrollToEnd();
      return true;
    } catch (error) {
      console.error('发表评论失败:', error);
      return false;
    }
  };

  const handleSend = async () => {
    setBusy(true);
    await addComment();
    setBusy(false);
  };

  // 验收：通过 → 已完成；打回 → 回到进行中（先把你写的意见发出去）
  const changeStatus = async (status: Task['status']) => {
    setBusy(true);
    try {
      if (!(await addComment())) return;
      await updateTask(task.id, { status });
      onClose();
    } catch (error) {
      console.error('更新任务状态失败:', error);
    } finally {
      setBusy(false);
    }
  };

  const taskType = taskTypes.find(t => t.id === task.taskTypeId);
  const status = STATUS[task.status];

  return (
    <ModalShell
      title={task.title}
      onClose={onClose}
      closeOnOverlay={!editing}
      headerExtra={
        !editing && (
          <button onClick={() => setEditing(true)} className="px-3 py-1 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50">
            编辑
          </button>
        )
      }
      footer={
        !editing && (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) { e.preventDefault(); handleSend(); } }}
              placeholder={task.status === 'REVIEW' ? '写验收意见（打回时会一并发给 agent，支持 Markdown）…' : '写评论…（支持 Markdown，Ctrl+Enter 发送）'}
              rows={3}
              maxLength={50000}
              className="w-full text-sm rounded-md border border-gray-200 bg-white px-3 py-2 transition-colors focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 resize-y"
            />
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                onClick={handleSend}
                disabled={busy || !text.trim()}
                className="px-3 py-1.5 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40"
              >
                发送评论
              </button>
              {task.status === 'REVIEW' && (
                <>
                  <button
                    onClick={() => changeStatus('IN_PROGRESS')}
                    disabled={busy}
                    className="px-3 py-1.5 text-sm rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-40"
                  >
                    打回
                  </button>
                  <button
                    onClick={() => changeStatus('DONE')}
                    disabled={busy}
                    className="px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-40"
                  >
                    通过，标为已完成
                  </button>
                </>
              )}
              {task.status === 'BLOCKED' && (
                <button
                  onClick={() => changeStatus('IN_PROGRESS')}
                  disabled={busy}
                  className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                >
                  补充后重新开始
                </button>
              )}
            </div>
          </>
        )
      }
    >
      {editing ? (
        <div className="px-8 pb-6">
          <TaskEditForm task={task} taskType={taskType} onCancel={leaveEdit} onSave={leaveEdit} />
        </div>
      ) : (
        <>
          {/* 状态与属性：宽松排开 */}
          <div className="px-8 pb-5 grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-4 border-b border-gray-100">
            <Field label="状态">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${status.className}`}>{status.label}</span>
            </Field>
            <Field label="优先级">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${PRIORITY[task.priority].className}`}>{PRIORITY[task.priority].label}</span>
            </Field>
            <Field label="大小">{SIZE[task.size] || '—'}</Field>
            <Field label="类别">{taskType?.name || '—'}</Field>
            <Field label="负责人">
              {task.user ? getUserDisplayName(task.user) : '未分配'}
              {task.user?.isAgent && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-xs">AI</span>}
            </Field>
            <Field label="截止日期">{formatDate(task.dueDate)}</Field>
            <Field label="所属目标">{task.goal?.title || '—'}</Field>
            <Field label={task.status === 'DONE' ? '完成时间' : task.startedAt ? '开始时间' : '创建时间'}>
              {formatDate(task.status === 'DONE' ? task.completedAt : task.startedAt || task.createdAt)}
            </Field>
          </div>

          {/* 内容 */}
          <div className="px-8 py-6 space-y-8">
            <section>
              <div className="text-xs font-medium text-gray-400 mb-2">描述</div>
              {task.description?.trim()
                ? <Markdown content={task.description} />
                : <div className="text-sm text-gray-400">（没有描述）</div>}
            </section>

            <section>
              <div className="text-xs font-medium text-gray-400 mb-2">完成标准</div>
              {task.acceptance?.trim()
                ? <Markdown content={task.acceptance} />
                : <div className="text-sm text-amber-600">（没写完成标准，agent 不能领取这个任务）</div>}
            </section>

            <section>
              <div className="text-xs font-medium text-gray-400 mb-3">过程记录</div>
              {loading ? (
                <div className="text-sm text-gray-400">加载中…</div>
              ) : comments.length === 0 ? (
                <div className="text-sm text-gray-400">还没有记录</div>
              ) : (
                <ul className="space-y-4">
                  {comments.map(c => (
                    <li key={c.id} className={`rounded-lg border p-4 ${c.kind === 'REPORT' ? 'border-amber-300 bg-amber-50/50' : 'border-gray-100'}`}>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <span className={`px-1.5 py-0.5 rounded ${KIND_LABEL[c.kind].className}`}>{KIND_LABEL[c.kind].text}</span>
                        <span className="text-gray-700">{getUserDisplayName(c.author as any)}</span>
                        {c.author.isAgent && <span className="px-1 rounded bg-indigo-100 text-indigo-700">AI</span>}
                        <span>{formatTime(c.createdAt)}</span>
                      </div>
                      <Markdown content={c.content} />
                    </li>
                  ))}
                </ul>
              )}
              <div ref={bottomRef} />
            </section>
          </div>
        </>
      )}
    </ModalShell>
  );
};
